import React, { useState, useEffect } from "react"
import { get, post, put, remove } from "../../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../../common/pagination"
import { AlertTriangle } from "lucide-react"

const PracticesManagement = () => {
  const [practices, setPractices] = useState([])
  const [showPopup, setShowPopup] = useState(false)
  const [practiceToEdit, setPracticeToEdit] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [practiceIdToDelete, setPracticeIdToDelete] = useState(null)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")
  const [hoveredRow, setHoveredRow] = useState(null)
  const [activeRow, setActiveRow] = useState(null)

  // Search Filter
  const filteredPractices = practices?.filter((practice) => {
    return Object.entries(practice).some(([key, value]) => {
      if (key === "practiceName") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.trim().toLowerCase())
      }
      return false
    })
  })

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentPractices = filteredPractices.slice(
    startIdx,
    startIdx + rowsPerPage,
  )
  const totalPages = Math.ceil(filteredPractices.length / rowsPerPage)

  // const currentFaxes = sortedFaxes.slice(startIdx, startIdx + rowsPerPage)
  // const totalPages = Math.ceil(filteredFaxes.length / rowsPerPage)

  useEffect(() => {
    loadPractice()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchPractices = async () => {
    try {
      const data = await get("/practice/fetchAllPractices")
      setPractices(data)
    } catch (error) {
      toast.error("Failed to fetch practices")
    }
  }

  const loadPractice = async () => {
    setLoading(true)
    await fetchPractices()
    setLoading(false)
  }

  const handleAddPractice = async (data) => {
    setActionLoading(true)

    const isEdit = !!data.practiceId
    const practiceId = data?.practiceId

    if (
      data?.practiceOldName &&
      data?.practiceOldName?.trim() === data?.practiceNewName?.trim()
    ) {
      toast.error("Practice old & new name should not be same")
      setActionLoading(false)
      return
    }

    try {
      if (isEdit) {
        await put(`/practice/updatePractice`, {
          practiceId,
          practiceOldName: data.practiceOldName,
          practiceNewName: data.practiceNewName,
        })
      } else {
        await post("/practice/savePractice", {
          practiceName: data.practiceNewName,
        })
      }

      fetchPractices()
      setShowPopup(false)
      setPracticeToEdit(null)
      toast.success(`Practice ${isEdit ? "updated" : "added"} successfully`)
    } catch (error) {
      toast.error(error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeletePractice = async () => {
    setActionLoading(true)
    try {
      await remove(`/practice/deletePractice/${practiceIdToDelete.practiceId}`)
      fetchPractices()
      setPracticeIdToDelete(null)
      toast.success("Practice removed successfully")
    } catch (error) {
      toast.error("Something went wrong")
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", width: "100%", overflow: "hidden" }}
        >
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <div className="setting-note">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder={`Search Practices`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                  style={{ width: "350px", paddingRight: "22px" }}
                />
                <button
                  type="button"
                  onClick={() => { setSearchText(""); setCurrentPage(1) }}
                  className="search-clear-btn"
                  style={{ visibility: searchText ? "visible" : "hidden" }}
                >
                  ✕
                </button>
              </div>
            </div>
            <button
              className="faxes-button"
              onClick={() => setShowPopup(true)}
              style={{ marginBottom: "5px" }}
            >
              Add Practice
            </button>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th" style={{ width: "80%" }}>
                    Practice Name
                  </th>

                  {/* <th className="faxes-th">Secret</th> */}
                  <th className="faxes-th" style={{ textAlign: "center" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentPractices.length > 0 ? (
                  currentPractices.map((practice) => (
                    <tr
                      key={practice.practiceId}
                      className={`fax-item${(hoveredRow === practice.practiceId || activeRow === practice.practiceId) ? " row-highlighted" : ""}`}
                      style={{ cursor: "unset" }}
                      onMouseEnter={() => {
                        setHoveredRow(practice.practiceId)
                        if (activeRow !== practice.practiceId) setActiveRow(null)
                      }}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="faxes-td">{practice.practiceName}</td>

                      <td className="faxes-td" style={{ textAlign: "center" }}>
                        <button
                          className="faxes-button"
                          onClick={() => { setPracticeToEdit(practice); setActiveRow(practice.practiceId) }}
                        >
                          Edit
                        </button>
                        <button
                          className="faxes-button delete-button"
                          onClick={() => { setPracticeIdToDelete(practice); setActiveRow(practice.practiceId) }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredPractices.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {filteredPractices.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={filteredPractices.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {(showPopup || practiceToEdit) && (
        <PracticeForm
          onSave={handleAddPractice}
          onClose={() => {
            setShowPopup(false)
            setPracticeToEdit(null)
          }}
          practiceToEdit={practiceToEdit}
          actionLoading={actionLoading}
        />
      )}

      {practiceIdToDelete && (
        <DeleteConfirmationPopup
          onConfirm={handleDeletePractice}
          onCancel={() => setPracticeIdToDelete(null)}
          actionLoading={actionLoading}
          practiceIdToDelete={practiceIdToDelete}
        />
      )}
    </div>
  )
}

const PracticeForm = ({ onClose, onSave, practiceToEdit, actionLoading }) => {
  const [practiceName, setPracticeName] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  // Load initial data if editing
  useEffect(() => {
    if (practiceToEdit?.practiceName) {
      setPracticeName(practiceToEdit.practiceName)
    }
  }, [practiceToEdit])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!practiceName.trim()) {
      setErrorMessage("Practice Name is required.")
      return
    }

    const specialCharRegex = /[^a-zA-Z0-9 _-]/
    if (specialCharRegex.test(practiceName.trim())) {
      setErrorMessage(
        "Practice Name must not contain special characters. Allow '_' and '-' only",
      )
      return
    }

    onSave({
      ...practiceToEdit, // in case you want to pass id or other fields
      practiceNewName: practiceName.trim(),
      practiceOldName: practiceToEdit?.practiceName,
    })
  }

  return (
    <div className="popup-overlay">
      <div className="popup">
        <div className="checkbox-popup-header">
          <h3 className="popup-title">
            {practiceToEdit ? `Update` : `Add`} Practice
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <form className="popup-group" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="practiceName">
              Practice Name: <span className="mandatory">*</span>
            </label>
            <input
              type="text"
              id="practiceName"
              value={practiceName}
              onChange={(e) => {
                setPracticeName(e.target.value)
                setErrorMessage("")
              }}
            />
          </div>

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="popup-buttons">
            <button
              className="popup-button cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="popup-button save" type="submit">
              {actionLoading ? `Saving ...` : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const DeleteConfirmationPopup = ({
  onConfirm,
  onCancel,
  actionLoading,
  practiceIdToDelete,
}) => (
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px", maxWidth: "700px" }}>
      {actionLoading ? (
        <div>Processing...</div>
      ) : (
        <>
          {/* <h3>Confirm Deletion</h3> */}
          <p>
            Are you sure you want to delete the practice{" "}
            <strong>{practiceIdToDelete?.practiceName}?</strong>
          </p>
          <p style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <AlertTriangle size={18} color="#d97706" />
            Deleting this practice will clean up all related associations —
            including fax numbers, admin, and user. This action is irreversible.
          </p>
          <div className="popup-buttons">
            <button className="cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)

export default PracticesManagement
